import { collection, doc, getDoc, getDocs, limit, orderBy, query, startAfter, where } from "firebase/firestore/lite";
import { auth, db, callFunction } from "./firebase.js";

/**
 * 서버 계약을 한 곳에 모은다. 화면은 여기만 부른다.
 *
 * 읽기(주제·아카이브)는 Firestore 규칙이 공개를 허용하므로 직접 읽고,
 * 쓰기는 전부 onCall 을 거친다 — 규칙이 클라이언트 쓰기를 전부 막아 뒀다.
 */

// ── 읽기 ──────────────────────────────────────────────

/** 정렬 두 가지. 인덱스는 infra/firestore.indexes.json 에 있다. */
export const TOPIC_SORTS = {
  popular: { label: "많이 붙은 주제", field: "debateCount", dir: "desc" },
  recent: { label: "새로 올라온 주제", field: "createdAt", dir: "desc" },
};

export const TOPIC_PAGE = 8;

/**
 * 주제는 매주 10개씩 **쌓인다.** 전체를 한 번에 읽으면 몇 달 안에
 * 느려지고 읽기 비용도 붙는다 — 커서로 끊어 읽는다.
 *
 * @returns {{topics: Array, cursor: any, done: boolean}} cursor 를 다시 넘기면 다음 장
 */
export async function listTopics({ sort = "popular", category = null, cursor = null, max = TOPIC_PAGE } = {}) {
  const s = TOPIC_SORTS[sort] ?? TOPIC_SORTS.popular;
  const parts = [collection(db, "topics"), where("isActive", "==", true)];
  if (category) parts.push(where("category", "==", category));
  parts.push(orderBy(s.field, s.dir));
  if (cursor) parts.push(startAfter(cursor));
  parts.push(limit(max));

  const snap = await getDocs(query(...parts));
  return {
    topics: snap.docs.map((d) => ({ id: d.id, ...d.data() })),
    cursor: snap.docs.at(-1) ?? null,
    done: snap.docs.length < max,
  };
}

/**
 * 지금 많이 붙는 주제 셋.
 *
 * `debateCount` 가 아니라 `hotScore`(최근 7일)로 줄 세운다. 누적으로 세우면
 * **옛 주제가 영원히 1등**이라 「지금」이라는 말이 거짓이 된다. 점수는 6시간
 * 배치가 날짜별 카운터를 합산해 적어 둔다(functions/src/hotTopics.js).
 *
 * isActive 를 쿼리에 같이 걸면 복합 인덱스가 필요해지므로 받아서 거른다.
 * 아무도 아직 안 붙은 날에는 전부 0 이라 빈 배열로 돌려준다 — 그때는
 * 「지금 많이 붙는」이라고 말할 근거가 없으니 아예 안 그리는 게 맞다.
 */
export async function hotTopics(n = 3) {
  const snap = await getDocs(query(
    collection(db, "topics"),
    orderBy("hotScore", "desc"),
    limit(n + 5),
  ));
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((t) => t.isActive === true && (t.hotScore ?? 0) > 0)
    .slice(0, n);
}

/**
 * 오늘의 주제 — 고정된 게 있으면 그것, 없으면 참여가 가장 많은 코어 주제.
 *
 * ★ 참여순만으로는 **새 주제가 영원히 못 올라온다.** debateCount 0 으로 시작하니
 * 이미 쌓인 주제를 이길 수 없다. 시의성 있는 주제를 띄우려면 따로 고정해야 한다.
 *
 * `pinnedUntil` 은 시각이라 **저절로 풀린다.** 뉴스 주제가 철 지난 뒤에도 첫 화면에
 * 남아 있는 것이 제일 나쁜 결과라서, 내리는 걸 사람 손에 맡기지 않는다.
 *
 * 쿼리를 둘로 나눈 이유: `isActive == true` 와 `pinnedUntil > now` 를 한 번에 걸면
 * 복합 인덱스가 필요해진다. 고정 주제는 많아야 몇 개라 받아서 걸러도 싸다.
 */
export async function featuredTopic() {
  const pinned = await getDocs(query(
    collection(db, "topics"),
    where("pinnedUntil", ">", new Date()),
    orderBy("pinnedUntil", "desc"),
    limit(3),
  ));
  const live = pinned.docs.find((d) => d.data().isActive === true);
  if (live) return { id: live.id, ...live.data() };

  const snap = await getDocs(query(
    collection(db, "topics"),
    where("core", "==", true),
    where("isActive", "==", true),
    orderBy("debateCount", "desc"),
    limit(1),
  ));
  const d = snap.docs[0];
  return d ? { id: d.id, ...d.data() } : null;
}

/**
 * 오늘 내 몫이 남았는지.
 *
 * ★ 화면에 「오늘 남은 토론 1」이 **상수로 박혀 있었다.** 이미 쓴 날에도 1 로 떠서
 * 거짓말을 하고 있었다. 숫자를 말할 거면 세야 한다.
 *
 * 한도가 1 이라 「오늘 만든 토론이 있는가」만 보면 된다 — 문서 하나 읽기다.
 * 집계 쿼리는 lite SDK 에 없다.
 *
 * 경계는 **KST 자정**이다. 서버의 dateKey(Asia/Seoul)와 같아야 한다 —
 * 다르면 밤 시간대에 화면과 서버가 어긋난다.
 *
 * 이건 **표시용이다.** 진짜 차단은 startDebate 트랜잭션이 한다.
 * 읽기에 실패하면 막지 않는다 — 표시 하나 때문에 시작을 못 하게 하지 않는다.
 */
export async function remainingToday() {
  const uid = auth.currentUser?.uid;
  if (!uid) return null;
  const kstNow = new Date(Date.now() + 9 * 3600 * 1000);
  const midnightKst = new Date(Date.UTC(
    kstNow.getUTCFullYear(), kstNow.getUTCMonth(), kstNow.getUTCDate()) - 9 * 3600 * 1000);
  try {
    const snap = await getDocs(query(
      collection(db, "debates"),
      where("uid", "==", uid),
      where("createdAt", ">=", midnightKst),
      orderBy("createdAt", "desc"),
      limit(1),
    ));
    return snap.empty ? 1 : 0;
  } catch {
    return null; // 모르면 숫자를 말하지 않는다
  }
}

export async function getTopic(topicId) {
  const snap = await getDoc(doc(db, "topics", topicId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

/**
 * 내 지난 토론.
 *
 * 규칙이 본인 것만 읽게 막아 두므로(firestore.rules) 서버를 거치지 않아도 된다.
 * 익명 사용자도 자기 기록은 보인다 — 다만 브라우저를 옮기면 못 본다.
 */
export async function listMyDebates({ cursor = null, max = 10 } = {}) {
  const uid = auth.currentUser?.uid;
  if (!uid) return { debates: [], cursor: null, done: true };

  const parts = [
    collection(db, "debates"),
    where("uid", "==", uid),
    orderBy("createdAt", "desc"),
  ];
  if (cursor) parts.push(startAfter(cursor));
  parts.push(limit(max));

  const snap = await getDocs(query(...parts));
  return {
    debates: snap.docs.map((d) => ({ id: d.id, ...d.data() })),
    cursor: snap.docs.at(-1) ?? null,
    done: snap.docs.length < max,
  };
}

/** 지난 토론 하나. 결과물이 문서에 그대로 들어 있다. */
export async function getDebate(debateId) {
  const snap = await getDoc(doc(db, "debates", debateId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

export async function listArchive({ sort = "recent", max = 20 } = {}) {
  const order = {
    recent: orderBy("publishedAt", "desc"),
    shaken: orderBy("convictionDelta", "asc"),   // 많이 내려간 순
    steady: orderBy("convictionDelta", "desc"),  // 많이 올라간 순
  }[sort];
  const snap = await getDocs(
    query(collection(db, "archive"), where("isPublic", "==", true), order, limit(max)),
  );
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

// ── 쓰기 (전부 서버를 거친다) ─────────────────────────

export const startDebate = (topicId, stance, convictionStart, consentResearch) =>
  callFunction("startDebate", { topicId, stance, convictionStart, consentResearch });

/** 연구·통계 이용 동의를 나중에 켜거나 끈다. 선택 동의라 철회가 자유로워야 한다. */
export const setResearchConsent = (agreed) =>
  callFunction("setResearchConsentFn", { agreed });

export const setDemographics = (demographics) =>
  callFunction("setDemographics", { demographics });

/** 규칙이 users/{uid} 를 본인에게도 막아 두므로, 설정 화면이 볼 두 값만 함수로 꺼낸다. */
export const getMyPrivacy = () => callFunction("getMyPrivacy", {});

/**
 * 결과 화면의 비교값. 계산은 서버가 한다 —
 * 표본이 적은 칸을 감추는 판단이 화면에 있으면 화면을 고치는 것만으로 뚫린다.
 */
export const peerCompare = (debateId) => callFunction("peerCompareFn", { debateId });

/** 이용약관·개인정보처리방침 동의. 필수라 로그인 시트가 막고 나서 부른다. */
export const acceptTerms = () => callFunction("acceptTermsFn", { agreed: true });

/** 「이런 거 다뤄주세요」. 낸 글은 주간 배치의 재료로만 쓰인다. */
export const suggestTopic = (text) => callFunction("suggestTopic", { text });

export const submitTurn = (debateId, seq, content) =>
  callFunction("submitDebateTurn", { debateId, seq, content });

export const finishSession = (debateId, convictionEnd) =>
  callFunction("finishDebateSession", { debateId, convictionEnd });

export const publishToArchive = (debateId, isPublic) =>
  callFunction("publishToArchive", { debateId, isPublic });

export const resumeDebate = () => callFunction("resumeDebate", {});
